import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DomErrorBoundary } from "@/components/DomErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clienti from "@/pages/Clienti";
import Erogatori from "@/pages/Erogatori";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Utenti from "@/pages/Utenti";
import Impostazioni from "@/pages/Impostazioni";
import Fatturazione from "@/pages/Fatturazione";
import Ricambi from "@/pages/Ricambi";
import KnowledgeBase from "@/pages/KnowledgeBase";
import PublicTicket from "@/pages/PublicTicket";
import QrLanding from "@/pages/QrLanding";
import DispenserHistory from "@/pages/DispenserHistory";
import PayHoursPublic from "@/pages/PayHoursPublic";
import PayHoursPublicSuccess from "@/pages/PayHoursPublicSuccess";
import AcquistoOreSuccess from "@/pages/AcquistoOreSuccess";
import PublicQuote from "@/pages/PublicQuote";
import PublicQuoteSuccess from "@/pages/PublicQuoteSuccess";
import VoicebotDemo from "@/pages/VoicebotDemo";
import WarrantyRegister from "@/pages/WarrantyRegister";
import WarrantyConfirm from "@/pages/WarrantyConfirm";

// If the app is opened with the legacy enobox.eu QR params (?QR=&D=&N=),
// serve the public landing (no auth) so customers can open tickets by scanning
// the existing sticker on the dispenser.
function RootDispatcher() {
  const [params] = useSearchParams();
  if (params.get("QR") && params.get("N")) return <QrLanding />;
  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  );
}

function DashboardOrTickets() {
  const { user } = useAuth();
  if (user?.role === "tecnico_distributore") return <Navigate to="/tickets" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <DomErrorBoundary>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/r/:serial" element={<PublicTicket />} />
          <Route path="/qr" element={<QrLanding />} />
          <Route path="/pay/hours/:token" element={<PayHoursPublic />} />
          <Route path="/pay/hours/:token/success" element={<PayHoursPublicSuccess />} />
          <Route path="/preventivo/:token" element={<PublicQuote />} />
          <Route path="/preventivo/:token/success" element={<PublicQuoteSuccess />} />
          <Route path="/garanzia" element={<WarrantyRegister />} />
          <Route path="/garanzia/conferma/:token" element={<WarrantyConfirm />} />
          <Route path="/" element={<RootDispatcher />}>
            <Route index element={<DashboardOrTickets />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="tickets/:id" element={<TicketDetail />} />
            <Route path="dispenser" element={<Erogatori />} />
            <Route path="dispenser/:serial/history" element={<DispenserHistory />} />
            <Route path="erogatori" element={<Navigate to="/dispenser" replace />} />
            <Route path="erogatori/:serial/history" element={<Navigate to="/dispenser" replace />} />
            <Route path="clienti" element={<Clienti />} />
            <Route path="ricambi" element={<Ricambi />} />
            <Route path="conoscenza" element={<KnowledgeBase />} />
            <Route
              path="utenti"
              element={
                <ProtectedRoute requireAdmin>
                  <Utenti />
                </ProtectedRoute>
              }
            />
            <Route
              path="impostazioni"
              element={
                <ProtectedRoute requireAdmin>
                  <Impostazioni />
                </ProtectedRoute>
              }
            />
            <Route
              path="fatturazione"
              element={
                <ProtectedRoute requireAdmin>
                  <Fatturazione />
                </ProtectedRoute>
              }
            />
            <Route
              path="voicebot"
              element={
                <ProtectedRoute requireAdmin>
                  <VoicebotDemo />
                </ProtectedRoute>
              }
            />
            <Route path="acquisto-ore/success" element={<AcquistoOreSuccess />} />
            <Route path="acquisto-ore/cancel" element={<AcquistoOreSuccess />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </DomErrorBoundary>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}
